<template>
  <portlet
      :icon="'fad fa-mobile'"
      :title="'App onboarding'"
      :footer="stage === 'form'"
  >
    <template slot="portletBody">
      <div v-if="stage === 'loading'" class="alert alert-info mb-0">Bezig met laden&hellip;</div>

      <div v-else-if="stage === 'error'" class="alert alert-danger mb-0">
        {{ loadError }}
        <button type="button" class="btn btn-link p-0 ml-2 align-baseline" @click="loadOnboardingState">
          Opnieuw proberen
        </button>
      </div>

      <div v-else-if="stage === 'overview'">
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <p class="mb-2">
            <strong>{{ customer.name }}</strong> werkt met de Workfix app.
          </p>
          <button v-if="mayManageAppOnboarding()" type="button" class="btn btn-primary mb-2 invite-another" @click="startOnboarding">
            <i class="fal fa-user-plus mr-2"></i> Gebruiker uitnodigen
          </button>
        </div>
        <customer-client-users-table
            :client-users="clientUsers"
            :busy="changingAccess"
            :read-only="!mayManageAppOnboarding()"
            @deactivate="deactivate"
            @reactivate="reactivate"
            @resendInvitation="resendInvitation"
        />
      </div>

      <div v-else-if="stage === 'invite'">
        <p>
          <strong>{{ customer.name }}</strong> werkt nog niet met de Workfix app.
        </p>
        <template v-if="mayManageAppOnboarding()">
          <p class="text-muted">
            Nodig de contactpersoon aan klantzijde uit voor het portaal. Die kan daar uren controleren
            en goedkeuren, verlofaanvragen goedkeuren of afkeuren, en documenten delen en bekijken.
          </p>
          <button type="button" class="btn btn-primary" @click="startOnboarding">
            <i class="fal fa-paper-plane mr-2"></i> Onboarding starten
          </button>
        </template>
      </div>

      <form v-else action @submit.prevent="submit">
        <div class="row">
          <div class="form-group col-lg-6">
            <label for="app-onboarding-firstname">Voornaam</label>
            <input
                v-validate="'required'"
                name="firstName"
                id="app-onboarding-firstname"
                type="text"
                class="form-control"
                v-model="form.firstName"
            />
            <span v-if="errors.first('firstName')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-6">
            <label for="app-onboarding-lastname">Achternaam</label>
            <input
                v-validate="'required'"
                name="lastName"
                id="app-onboarding-lastname"
                type="text"
                class="form-control"
                v-model="form.lastName"
            />
            <span v-if="errors.first('lastName')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-6">
            <label for="app-onboarding-email">E-mailadres</label>
            <input
                v-validate="'required|email'"
                name="email"
                data-vv-as="E-mailadres"
                id="app-onboarding-email"
                type="email"
                class="form-control"
                v-model="form.email"
                @blur="checkEmail"
            />
            <span v-if="errors.first('email')" class="small text-danger">{{ errors.first('email') }}</span>
            <span v-else-if="emailError" class="small text-danger">{{ emailError }}</span>
          </div>
          <div class="form-group col-lg-6">
            <label for="app-onboarding-phone">Telefoonnummer</label>
            <input
                v-validate="'required'"
                name="phone"
                id="app-onboarding-phone"
                type="text"
                class="form-control"
                v-model="form.phone"
            />
            <span v-if="errors.first('phone')" class="small text-danger">Dit veld is verplicht</span>
          </div>
        </div>
      </form>
    </template>

    <div v-if="stage === 'form'" slot="portletFooter" class="col text-right">
      <button type="button" class="btn btn-outline-secondary mr-2" :disabled="submitting" @click="cancelOnboarding">
        Annuleren
      </button>
      <button type="button" class="btn btn-primary" :disabled="submitting" @click="submit">
        <i class="fal fa-paper-plane mr-2"></i> Uitnodiging versturen
      </button>
    </div>
  </portlet>
</template>

<script>
import Vue from 'vue';
import {mapGetters} from 'vuex';
import {swalModals} from '@/components/mixins/swalModals';
import customerClientUsersTable from '@/components/customer/customerClientUsersTable';
import {mayManageAppOnboarding} from '@/utils/appOnboardingAccess';
import {
  deactivatedMessage,
  deactivationConfirmation,
  noClientUsersYet,
  reactivatedMessage,
  reinvitedMessage,
  replaceClientUser,
} from '@/utils/clientUserOverview';
import {
  describeEmailAvailabilityOutcome,
  emptyInvitationForm,
  findCustomerRoleId,
  prefillFromCustomer,
} from '@/utils/clientUserOnboarding';

const MISSING_CUSTOMER_ROLE = 'Er is voor deze vestiging geen klantrol ingericht. Maak eerst een rol met het recht IS_CUSTOMER aan.';

const MISSING_DEPARTMENT = 'Deze klant heeft geen vestiging, dus er kan geen klantrol worden bepaald.';

const UNEXPECTED_ONBOARDING_FAILURE = 'Er ging iets mis bij het versturen van de uitnodiging. Probeer het opnieuw.';

const UNEXPECTED_LOAD_FAILURE = 'Er ging iets mis bij het ophalen van de app-status. Probeer het opnieuw.';

const UNEXPECTED_ACCESS_FAILURE = 'Er ging iets mis bij het aanpassen van de toegang. Probeer het opnieuw.';

export default {
  props: ['id', 'customer'],
  mixins: [swalModals],
  components: {customerClientUsersTable},
  data() {
    return {
      stage: 'loading',
      submitting: false,
      changingAccess: false,
      emailError: null,
      checkedEmail: null,
      loadError: null,
      prefilledForm: null,
      clientUsers: [],
      form: emptyInvitationForm(),
    };
  },
  computed: {
    ...mapGetters('roles', ['getRoles']),
    departmentName() {
      return (this.customer && this.customer.department) || null;
    },
    hasEditedTheInvitation() {
      return this.stage === 'form' && JSON.stringify(this.form) !== JSON.stringify(this.prefilledForm);
    },
  },
  methods: {
    mayManageAppOnboarding,
    async loadOnboardingState() {
      this.stage = 'loading';
      this.loadError = null;

      try {
        await this.applyOnboardingState();
      } catch (unexpectedFailure) {
        this.showLoadError(UNEXPECTED_LOAD_FAILURE);
      }
    },
    async applyOnboardingState() {
      const outcome = await this.$store.dispatch('organizations/findByCustomerId', {
        ApiClient: Vue.prototype.$ApiClient,
        departments: this.$store.getters.getUserData.departments,
        customerId: this.customer.id,
      });

      if (!outcome.ok) {
        this.showLoadError(outcome.message);

        return;
      }

      const listed = await this.clientUsersOf(outcome.organization);

      if (!listed.ok) {
        this.showLoadError(listed.message);

        return;
      }

      this.clientUsers = listed.clientUsers;
      this.stage = this.landingStage();
    },
    clientUsersOf(organization) {
      if (!organization) {
        return noClientUsersYet();
      }

      return this.$store.dispatch('clientUsers/fetchForOrganization', {
        ApiClient: Vue.prototype.$ApiClient,
        organizationId: organization.id,
      });
    },
    showLoadError(message) {
      this.loadError = message;
      this.stage = 'error';
    },
    landingStage() {
      return this.clientUsers.length ? 'overview' : 'invite';
    },
    startOnboarding() {
      const prefilled = this.clientUsers.length ? emptyInvitationForm() : prefillFromCustomer(this.customer);

      this.form = {...prefilled};
      this.prefilledForm = prefilled;
      this.emailError = null;
      this.checkedEmail = null;
      this.stage = 'form';
      this.$emit('unsavedChanges', false);
    },
    cancelOnboarding() {
      this.stage = this.landingStage();
      this.emailError = null;
      this.$validator.reset();
      this.$emit('unsavedChanges', false);
    },
    async checkEmail() {
      this.emailError = null;

      if (!this.form.email || this.form.email === this.checkedEmail) {
        return;
      }

      const checkedAddress = this.form.email;
      const outcome = describeEmailAvailabilityOutcome(await this.$store.dispatch('clientUsers/checkEmailAvailability', {
        ApiClient: Vue.prototype.$ApiClient,
        email: checkedAddress,
        customerId: this.customer.id,
      }));

      if (checkedAddress !== this.form.email) {
        return;
      }

      this.checkedEmail = outcome.ok ? checkedAddress : null;

      if (!outcome.ok) {
        this.emailError = outcome.fieldError || outcome.message;
      }
    },
    async submit() {
      if (this.submitting) {
        return;
      }

      this.submitting = true;

      try {
        await this.attemptInvitation();
      } catch (unexpectedFailure) {
        this.$toasted.global.failedRequest({message: UNEXPECTED_ONBOARDING_FAILURE});
      } finally {
        this.submitting = false;
      }
    },
    async attemptInvitation() {
      if (!await this.$validator.validate()) {
        this.$toasted.global.failedFormRequest();

        return;
      }

      await this.checkEmail();

      if (this.emailError) {
        return;
      }

      if (!this.departmentName) {
        this.$toasted.global.failedRequest({message: MISSING_DEPARTMENT});

        return;
      }

      const roleId = findCustomerRoleId(this.getRoles, this.departmentName);

      if (!roleId) {
        this.$toasted.global.failedRequest({message: MISSING_CUSTOMER_ROLE});

        return;
      }

      await this.invite(roleId);
    },
    async invite(roleId) {
      this.$store.commit('setLoading', true);

      try {
        const outcome = await this.$store.dispatch('clientUsers/onboardWithPortalAccount', {
          ApiClient: Vue.prototype.$ApiClient,
          form: this.form,
          customer: this.customer,
          departmentName: this.departmentName,
          roleId,
        });

        if (!outcome.ok) {
          this.checkedEmail = null;
          this.emailError = outcome.emailError;
          this.$toasted.global.failedRequest({message: outcome.message});

          return;
        }

        this.$emit('unsavedChanges', false);
        this.$toasted.global.successRequest({
          message: 'De uitnodiging is verstuurd naar ' + this.form.email,
        });
      } finally {
        this.$store.commit('setLoading', false);
      }

      await this.loadOnboardingState();
    },
    async deactivate(clientUser) {
      const confirmation = await this.swalConfirmAction(deactivationConfirmation(this.clientUsers, clientUser));

      if (!confirmation || !confirmation.value) {
        return;
      }

      await this.changeAccess('clientUsers/deactivate', clientUser, deactivatedMessage);
    },
    async reactivate(clientUser) {
      await this.changeAccess('clientUsers/reactivate', clientUser, reactivatedMessage);
    },
    async changeAccess(action, clientUser, announce) {
      await this.exclusively(async () => {
        const outcome = await this.$store.dispatch(action, {ApiClient: Vue.prototype.$ApiClient, clientUser});

        if (!outcome.ok) {
          this.$toasted.global.failedRequest({message: outcome.message});

          return;
        }

        this.clientUsers = replaceClientUser(this.clientUsers, outcome.clientUser);
        this.$toasted.global.successRequest({message: announce(outcome.clientUser)});
      });
    },
    async resendInvitation(clientUser) {
      await this.exclusively(async () => {
        const outcome = await this.$store.dispatch('clientUsers/resendInvitation', {
          ApiClient: Vue.prototype.$ApiClient,
          clientUser,
        });

        if (!outcome.ok) {
          this.$toasted.global.failedRequest({message: outcome.message});

          return;
        }

        this.$toasted.global.successRequest({message: reinvitedMessage(clientUser, outcome)});
      });
    },
    async exclusively(change) {
      if (this.changingAccess) {
        return;
      }

      this.changingAccess = true;

      try {
        await change();
      } catch (unexpectedFailure) {
        this.$toasted.global.failedRequest({message: UNEXPECTED_ACCESS_FAILURE});
      } finally {
        this.changingAccess = false;
      }
    },
  },
  watch: {
    form: {
      handler: function () {
        this.$emit('unsavedChanges', this.hasEditedTheInvitation);
      },
      deep: true,
    },
  },
  created() {
    this.loadOnboardingState();
  },
};
</script>
